import type { Dispatch, SetStateAction } from 'react';

export type ValidationDetail = {
    building_firm_name_corrected?: boolean;
    city_name_corrected?: boolean;
    gen_pre_st_info_retained?: boolean;
    post_st_info_retained?: boolean;
    secondary_num_retained?: boolean;
    state_corrected?: boolean;
    street_corrected?: boolean;
    zip_code_corrected?: boolean;
};

type AddressAttribute = {
    name: string;
    value: string;
};

export type Suggestion = {
    additional_attributes: AddressAttribute[];
    format?: string;
    global_address_key: string;
    text: string;
};

export enum Confidence {
    interactionRequired = 'Interaction required',
    noMatches = 'No matches',
    premisesPartial = 'Premises partial',
    streetPartial = 'Street partial',
    verifiedMatch = 'Verified match',
    multipleMatches = 'Multiple matches',
}

/**
 * When using specific layouts, either `EDQ NA SFCC 5-Line Default` or `Petsmart CAN`,
 * the address data in the response is structured as follows:
 *
 * ```json
 * "addresses_formatted": [
 *   {
 *     "layout_name": "EDQ NA SFCC 5-Line Default",
 *     "address": {
 *       "address.addressLine1": "9 Porter Cir",
 *       "address.addressLine2": "Apt a",
 *       "address.locality": "Cambridge",
 *       "address.province": "MA",
 *       "address.postalCode": "02140-2106"
 *     }
 *   }
 * ]
 * ```
 *
 * The address information is extracted from the `addresses_formatted` array based on the
 * specified `layout_name`. This allows us to process and utilize the address data accordingly.
 *
 * Below is the TypeScript type definition for the formatted address structure:
 *
 * @export
 * @type ExperianAddress
 */
export type ExperianAddressLayout = {
    layout_name: string;
    address: {
        'address.addressLine1': string; // First line of the address.
        'address.addressLine2'?: string; // Second line of the address (optional).
        'address.locality': string; // City or locality.
        'address.province': string; // State or province.
        'address.postalCode': string; // Postal or ZIP code.
        'Subpremise Retained'?: string; // Subpremise information (optional).
    };
};

export type ExperianAddress = {
    address_line_1: string;
    address_line_2: string;
    address_line_3: string;
    locality: string;
    region: string;
    postal_code: string;
    country: string;
};

export type NoMatchesResponse = {
    result: {
        more_results_available: boolean;
        confidence: Confidence.noMatches;
    };
};

export type PartialResponse = {
    more_results_available: boolean;
    suggestions_key: string;
    suggestions_prompt: string;
    suggestions: Suggestion[];
    validation_detail?: ValidationDetail;
};

export type MultipleMatchesResponse = {
    result: PartialResponse & {
        confidence: Confidence.multipleMatches;
        suggestions: Suggestion[];
    };
};

export type InteractionRequiredResponseLayout = {
    result: {
        validation_detail?: ValidationDetail;
        confidence: Confidence;
        global_address_key: string;
        addresses_formatted: ExperianAddressLayout[];
    };
};

export type InteractionRequiredResponse = {
    result: {
        validation_detail?: ValidationDetail;
        confidence: Confidence;
        global_address_key: string;
        address: {
            address_line_1: string;
            address_line_2: string;
            address_line_3: string;
            locality: string;
            region: string;
            postal_code: string;
            country: string;
        };
    };
};

export type PremisesPartialResponse = PartialResponse & {
    confidence: Confidence.premisesPartial;
};

export type StreetPartialResponse = {
    result: PartialResponse & {
        confidence: Confidence.streetPartial;
    };
};

/** Verified match response */
export type VerifiedMatchResponseLayout = {
    result: {
        confidence: Confidence;
        validation_detail: ValidationDetail;
        global_address_key: string;
        addresses_formatted: ExperianAddressLayout[];
    };
};

export type VerifiedMatchResponse = {
    result: {
        confidence: Confidence;
        validation_detail: ValidationDetail;
        global_address_key: string;
        address: ExperianAddress;
    };
};

/** TODO: ADD THE TYPE IN CASE WHEN THE ADDRESS KEY IS INVALID */
export type ExperianFormatAddressResponse = VerifiedMatchResponseLayout;

export type ExperianResponseLayout =
    | StreetPartialResponse
    | MultipleMatchesResponse
    | VerifiedMatchResponseLayout
    | NoMatchesResponse
    | InteractionRequiredResponseLayout;

export type ExperianResponse =
    | StreetPartialResponse
    | MultipleMatchesResponse
    | NoMatchesResponse
    | {
          result: {
              validation_detail?: ValidationDetail;
              confidence: Confidence;
              global_address_key: string;
              address: {
                  address_line_1: string;
                  address_line_2: string;
                  address_line_3: string;
                  locality: string;
                  region: string;
                  postal_code: string;
                  country: string;
              };
          };
      };

export type AddressType = 'form' | 'id';

export type FormProps = {
    firstName: string;
    lastName: string;
    phone?: string;
    email?: string;
    address: {
        streetAddress: string;
        apt?: string;
        city: string;
        state: string;
        zipCode: string;
    };
    petPreferences?: {
        [key: string]: boolean | undefined;
    };
    isTextOptedIn?: boolean;
    isEmailOptedIn?: boolean;
    isTextOptedOut?: boolean;
    isEmailOptedOut?: boolean;
    addressType: AddressType;
    isPrimary?: boolean;
    nameOnCard?: string;
    expirationDate?: string;
    selectedAddressId?: string;
    cardId?: number;
    ccToken?: string;
    isExistingEmail?: boolean;
};

export type ValidatedFormProps = FormProps & {
    gak: string;
};

export type ExperianModalType = {
    experianResponse: ExperianResponseLayout | null;
    saveSelectedAddress: (key: FormProps) => void;
    loading: boolean;
    setIsLoading: Dispatch<SetStateAction<boolean>>;
};
